import * as THREE from 'three';
import * as util from './util';

const minZoom = 0.3;
const maxZoom = 10.0;
const defaultCamDist = 1.5;

export class SceneCamera {
  constructor(cameraObj, imageEngine) {
    cameraObj.rotation.order = 'YXZ';
    this.imageEngine = imageEngine;
    this.obj = cameraObj;
    this._camDist = defaultCamDist;
  }

  updateAspectRatio(ratio) {
    const camera = this.obj;
    camera.aspect = ratio;
    camera.updateProjectionMatrix();
  }

  lookAtObject(object) {
    const camera = this.obj;
    const oldRotX = camera.rotation.x;
    const oldRotY = camera.rotation.y;

    const bbox = new THREE.Box3().setFromObject(object);
    camera.rotation.x = 0;
    camera.rotation.y = 0;
    camera.position.x = (bbox.min.x + bbox.max.x) / 2;
    camera.position.y = (bbox.min.y + bbox.max.y) / 2;
    camera.position.z = (bbox.min.z + bbox.max.z) / 2 + this._camDist;
    this.rotateCamera(oldRotY, oldRotX);
  }

  zoomCamera(zoom) {
    const camera = this.obj;
    camera.translateZ(-this._camDist);
    this._camDist += zoom;
    this._camDist = Math.max(minZoom, this._camDist);
    this._camDist = Math.min(maxZoom, this._camDist);
    camera.translateZ(this._camDist);
    if (this.imageEngine && this.imageEngine.facade) {
      this.imageEngine.facade.onCameraZoom();
    }
  }

  setCameraZoom(zoom) {
    const camera = this.obj;
    camera.translateZ(-this._camDist);
    this._camDist = zoom;
    this._camDist = Math.max(minZoom, this._camDist);
    this._camDist = Math.min(maxZoom, this._camDist);
    camera.translateZ(this._camDist);
    if (this.imageEngine && this.imageEngine.facade) {
      this.imageEngine.facade.onCameraZoom();
    }
  }

  rotateCamera(dx, dy) {
    const camera = this.obj;
    camera.translateZ(-this._camDist);

    camera.rotation.x += dy;
    camera.rotation.y += dx;
    camera.rotation.x = Math.min(camera.rotation.x, Math.PI / 2);
    camera.rotation.x = Math.max(camera.rotation.x, -Math.PI / 2);

    camera.translateZ(this._camDist);
  }

  setCameraRotation(x, y) {
    const camera = this.obj;
    camera.translateZ(-this._camDist);

    if (x !== undefined) {
      camera.rotation.y = x;
    }
    if (y !== undefined) {
      camera.rotation.x = y;
      camera.rotation.x = Math.min(camera.rotation.x, Math.PI / 2);
      camera.rotation.x = Math.max(camera.rotation.x, -Math.PI / 2);
    }

    camera.translateZ(this._camDist);
  }

  panCamera(dx, dy) {
    const camera = this.obj;
    camera.translateX(dx);
    camera.translateY(dy);
  }

  zoomToFit(renderer, object, margin) {
    if (margin === undefined) margin = 0;
    var size = new THREE.Vector2();
    renderer.getSize(size);

    var w = size.width;
    var h = size.height;
    var maxClipX = (w / 2 - margin) / (w / 2);
    var maxClipY = (h / 2 - margin) / (h / 2);

    var dist = zoomToFitCalc.call(
      this,
      object,
      minZoom,
      maxZoom,
      maxClipX,
      maxClipY,
      camDist => {
        const prevCamDist = this._camDist;
        this._camDist = camDist;
        if (this.imageEngine && this.imageEngine.facade) {
          this.imageEngine.facade.onCameraZoom();
        }
        this._camDist = prevCamDist;
      }
    );
    this.setCameraZoom(dist);
  }

  getCameraDistance() {
    return this._camDist;
  }
}

function zoomToFitCalc(
  object,
  minDist,
  maxDist,
  maxClipX,
  maxClipY,
  onTestCameraZoom
) {
  const camera = this.obj;
  var precision = 0.00001;
  var prevDist = this._camDist;
  var currDist;
  while (maxDist - minDist > precision) {
    currDist = (minDist + maxDist) / 2;
    camera.translateZ(currDist - prevDist);
    camera.updateMatrixWorld();
    onTestCameraZoom(currDist);
    prevDist = currDist;

    const vertices = util.getVertices(object);
    var success = true;
    for (var i = 0; i < vertices.length; i++) {
      var vec3 = new THREE.Vector3(vertices[i].x, vertices[i].y, vertices[i].z);
      var screenCoords = vec3.project(camera);
      if (
        screenCoords.x < -maxClipX ||
        screenCoords.x > maxClipX ||
        screenCoords.y < -maxClipY ||
        screenCoords.y > maxClipY
      ) {
        success = false;
        break;
      }
    }
    if (success) {
      maxDist = currDist;
    } else {
      minDist = currDist;
    }
  }
  camera.translateZ(this._camDist - currDist);

  return maxDist;
}
